import React, { useEffect, useId, useRef, useState } from 'react'
import { useControlledState } from '@react-stately/utils'
import { AriaProps } from '../../types'
import { Modal } from '../../../core-components/modal/Modal'
import { Box, Divider, Flex } from '../../../core-components'

type Props = {
  ModalSpecificProvider: React.ComponentType<{
    children: React.ReactNode
    value: { onClose: () => void; titleId: string }
  }>
  children: React.ReactNode
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  size: 22 | 26 | 28 | 31
  trigger: ((triggerProps: AriaProps) => React.ReactNode) | undefined
}

export const CoreModal = ({ children, size, ModalSpecificProvider, ...props }: Props) => {
  const titleId = useId()
  const [isModalOpen, setIsModalOpen] = useControlledState(props.isOpen, false, props.onOpenChange)

  return (
    <Modal
      aria-labelledby={titleId}
      animation='fadeInFromBottom'
      isOpen={isModalOpen}
      onOpenChange={setIsModalOpen}
      w={size}
      m={8}
      {...props}
    >
      <Flex
        h='100%'
        flexDirection='column'
        bg='bg/modal'
        borderRadius={12}
        boxShadow='border/on-major'
      >
        <ModalSpecificProvider
          value={{
            titleId,
            onClose: () => setIsModalOpen(false),
          }}
        >
          {children}
        </ModalSpecificProvider>
      </Flex>
    </Modal>
  )
}

export const CoreModalContent = ({
  children,
  header,
  footer,
  hasHeaderDivider,
}: {
  children?: React.ReactNode
  footer: React.ReactNode
  hasHeaderDivider?: boolean
  header: React.ReactNode
}) => {
  const childrenContainerRef = useRef<HTMLDivElement>(null)

  const { isOverflowing, isScrolledToBottom, isScrolledToTop } =
    useOverflowData(childrenContainerRef)
  const showHeaderDivider = hasHeaderDivider || (isOverflowing && !isScrolledToTop)
  const showFooterDivider = isOverflowing && !isScrolledToBottom
  return (
    <>
      {header}
      <Divider mx={7} mt='-1px' opacity={showHeaderDivider ? 1 : 0} />
      {children ? (
        <Box overflow='auto' ref={childrenContainerRef} pt={hasHeaderDivider ? 7 : 0}>
          {children}
        </Box>
      ) : null}
      <Divider mb='-1px' opacity={showFooterDivider ? 1 : 0} />
      {footer}
    </>
  )
}

const useOverflowData = (ref: React.RefObject<HTMLElement | null>) => {
  const [isScrolledToTop, setIsScrolledToTop] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  useEffect(() => {
    if (ref.current) {
      setIsOverflowing(ref.current.scrollHeight > ref.current.clientHeight)
    }
    const calculateScrollPosition = () => {
      if (ref.current) {
        setIsScrolledToTop(ref.current.scrollTop === 0)
        setIsScrolledToBottom(
          ref.current.scrollTop + ref.current.clientHeight === ref.current.scrollHeight
        )
      }
    }
    calculateScrollPosition()
    const element = ref.current
    element?.addEventListener('scroll', calculateScrollPosition, { passive: true })
    return () => {
      element?.removeEventListener('scroll', calculateScrollPosition)
    }
  }, [ref])
  return { isOverflowing, isScrolledToBottom, isScrolledToTop }
}
